const UNLOCODE_SOURCES = [
  { country: 'MX', path: '/UNLOCODE/unlocode.MX.json' },
  { country: 'US', path: '/UNLOCODE/unlocode.US.json' },
  { country: 'CA', path: '/UNLOCODE/unlocode.CA.json' },
  { country: 'GT', path: '/UNLOCODE/unlocode.GT.json' },
];

const LOCODE_PATTERN = /\b([A-Z]{2}[A-Z0-9]{3})\b/;

let loadPromise = null;
let cachedEntries = null;
let cachedLookups = null;

const locationResolutionCache = new Map();

const normalizeText = value =>
  String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const sanitizeLocationQuery = value =>
  String(value || '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[-–—>\u2190-\u21ff]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatDisplayLabel = entry => {
  const parts = [];
  const name =
    entry.nameWoDiacritics ||
    entry.name ||
    entry.location ||
    entry.locode;
  if (name) {
    parts.push(name);
  }
  if (entry.subdivision) {
    parts.push(entry.subdivision);
  }
  if (entry.country) {
    parts.push(entry.country);
  }
  const label = parts.join(', ');
  return label ? `${label} (${entry.locode})` : entry.locode;
};

const COORDINATE_PATTERN =
  /^\s*(\d{2})(\d{2})(\d{0,2})([NS])\s+(\d{3})(\d{2})(\d{0,2})([EW])\s*$/;

const parseCoordinateComponent = (degrees, minutes, seconds, direction) => {
  const deg = Number(degrees);
  const min = Number(minutes);
  const sec = seconds ? Number(seconds) : 0;
  if (Number.isNaN(deg) || Number.isNaN(min) || Number.isNaN(sec)) {
    return null;
  }
  let value = deg + min / 60 + sec / 3600;
  if (direction === 'S' || direction === 'W') {
    value = -value;
  }
  return value;
};

const parseCoordinates = rawValue => {
  if (!rawValue) {
    return null;
  }
  const match = String(rawValue).match(COORDINATE_PATTERN);
  if (!match) {
    return null;
  }
  const [, latDeg, latMin, latSec, latDir, lonDeg, lonMin, lonSec, lonDir] = match;
  const latitude = parseCoordinateComponent(latDeg, latMin, latSec, latDir);
  const longitude = parseCoordinateComponent(lonDeg, lonMin, lonSec, lonDir);
  if (latitude == null || longitude == null) {
    return null;
  }
  return { latitude, longitude };
};

const buildLookups = entries => {
  const byCode = new Map();
  const byDisplay = new Map();
  for (const entry of entries) {
    byCode.set(entry.locode, entry);
    if (entry.normalizedDisplay) {
      byDisplay.set(entry.normalizedDisplay, entry);
    }
  }
  return { byCode, byDisplay };
};

const resolveSourceUrl = path => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  const publicUrl =
    (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
  if (publicUrl) {
    const trimmed = publicUrl.endsWith('/') ? publicUrl.slice(0, -1) : publicUrl;
    return `${trimmed}${path}`;
  }
  return path;
};

async function loadUnlocodeData() {
  if (cachedEntries) {
    return cachedEntries;
  }

  if (!loadPromise) {
    loadPromise = Promise.all(
      UNLOCODE_SOURCES.map(async source => {
        const response = await fetch(resolveSourceUrl(source.path));
        if (!response.ok) {
          throw new Error(`No se pudieron cargar los UNLOCODE de ${source.country}`);
        }
        return response.json();
      })
    )
      .then(results => {
        const flattened = results.flat();
        cachedEntries = flattened.map(raw => {
          const display = formatDisplayLabel(raw);
          const normalizedDisplay = normalizeText(display);
          const searchTokens = [
            raw.locode,
            raw.name,
            raw.nameWoDiacritics,
            raw.location,
            raw.subdivision,
            raw.country,
            display,
          ]
            .filter(Boolean)
            .map(normalizeText)
            .join(' ');

          const parsedCoordinates = parseCoordinates(raw.coordinates);

          return {
            locode: raw.locode,
            country: raw.country,
            subdivision: raw.subdivision || '',
            name: raw.name || '',
            nameWoDiacritics: raw.nameWoDiacritics || '',
            display,
            normalizedDisplay,
            search: searchTokens,
            coordinates: raw.coordinates || '',
            latitude: parsedCoordinates?.latitude ?? null,
            longitude: parsedCoordinates?.longitude ?? null,
          };
        });
        cachedLookups = null;
        return cachedEntries;
      })
      .catch(error => {
        loadPromise = null;
        throw error;
      });
  }

  return loadPromise;
}

const ensureLookups = async () => {
  if (!cachedEntries) {
    await loadUnlocodeData();
  }
  if (!cachedLookups) {
    cachedLookups = buildLookups(cachedEntries);
  }
  return cachedLookups;
};

export async function searchUnlocodes(query, limit = 12) {
  const trimmed = normalizeText(query);
  if (!trimmed || trimmed.length < 2) {
    return [];
  }

  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (!tokens.length) {
    return [];
  }

  const entries = await loadUnlocodeData();

  const matches = [];
  for (const entry of entries) {
    let matched = true;
    let score = 0;
    for (const token of tokens) {
      const idx = entry.search.indexOf(token);
      if (idx === -1) {
        matched = false;
        break;
      }
      score += idx;
    }
    if (matched) {
      matches.push({ entry, score });
    }
  }

  matches.sort((a, b) => {
    if (a.score !== b.score) {
      return a.score - b.score;
    }
    return a.entry.display.localeCompare(b.entry.display);
  });

  return matches.slice(0, limit).map(match => match.entry);
}

export async function getUnlocodeByCode(code) {
  const normalized = String(code || '')
    .toUpperCase()
    .trim();
  if (!normalized) {
    return null;
  }
  const lookups = await ensureLookups();
  return lookups.byCode.get(normalized) || null;
}

export async function resolveLocationLabel(label) {
  const rawValue = String(label || '').trim();
  if (!rawValue) {
    return null;
  }

  if (locationResolutionCache.has(rawValue)) {
    return locationResolutionCache.get(rawValue);
  }

  try {
    const lookups = await ensureLookups();
    const codeMatch = rawValue.toUpperCase().match(LOCODE_PATTERN);
    if (codeMatch) {
      const byCode = lookups.byCode.get(codeMatch[1]);
      if (byCode && byCode.latitude != null && byCode.longitude != null) {
        locationResolutionCache.set(rawValue, byCode);
        return byCode;
      }
    }

    const normalizedDisplay = normalizeText(rawValue.replace(/\([^)]*\)/g, ' '));
    if (normalizedDisplay) {
      const byDisplay = lookups.byDisplay.get(normalizedDisplay);
      if (byDisplay && byDisplay.latitude != null && byDisplay.longitude != null) {
        locationResolutionCache.set(rawValue, byDisplay);
        return byDisplay;
      }
    }

    const cleanedQuery = sanitizeLocationQuery(rawValue);
    if (cleanedQuery.length >= 2) {
      const candidates = await searchUnlocodes(cleanedQuery, 3);
      const withCoordinates = candidates.find(
        entry => entry.latitude != null && entry.longitude != null
      );
      if (withCoordinates) {
        locationResolutionCache.set(rawValue, withCoordinates);
        return withCoordinates;
      }
    }

    if (rawValue.length >= 2 && cleanedQuery !== rawValue) {
      const fallbackCandidates = await searchUnlocodes(rawValue, 3);
      const fallback = fallbackCandidates.find(
        entry => entry.latitude != null && entry.longitude != null
      );
      if (fallback) {
        locationResolutionCache.set(rawValue, fallback);
        return fallback;
      }
    }

    locationResolutionCache.set(rawValue, null);
    return null;
  } catch (error) {
    console.error('No se pudo resolver el UNLOCODE', error);
    locationResolutionCache.set(rawValue, null);
    return null;
  }
}
